// ============================================================
// audio/sfx.js — One-shot sound effects
// ============================================================
import { state } from '../state.js';
import { SCALE } from '../constants.js';

// ---- Click pop (plushie click) ----
export function playClickSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sine';
  const note = SCALE[5 + Math.floor(Math.random() * 5)];
  osc.frequency.value = note;
  const env = state.audioCtx.createGain();
  const now = state.audioCtx.currentTime;
  env.gain.setValueAtTime(0.15, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.2);
}

// ---- Panel open (short ascending chirp) ----
export function playPanelOpenSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(440, now);
  osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(0.1, now + 0.01);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
}

// ---- Panel close (short descending chirp) ----
export function playPanelCloseSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(880, now);
  osc.frequency.exponentialRampToValueAtTime(440, now + 0.08);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(0.1, now + 0.01);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
}

// ---- Purchase arpeggio (shop buy) ----
export function playPurchaseSound() {
  if (!state.audioCtx) return;
  const now = state.audioCtx.currentTime;
  [523.25, 659.25, 783.99].forEach((freq, i) => {
    const osc = state.audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const env = state.audioCtx.createGain();
    env.gain.setValueAtTime(0, now + i * 0.08);
    env.gain.linearRampToValueAtTime(0.12, now + i * 0.08 + 0.03);
    env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.25);
    osc.connect(env).connect(state.audioCtx.destination);
    osc.start(now + i * 0.08);
    osc.stop(now + i * 0.08 + 0.3);
  });
}

// ---- Alarm beeps (bomb mode activation) ----
export function playAlarmSound() {
  if (!state.audioCtx) return;
  const now = state.audioCtx.currentTime;
  for (let i = 0; i < 3; i++) {
    const osc = state.audioCtx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 600;
    const env = state.audioCtx.createGain();
    env.gain.setValueAtTime(0.08, now + i * 0.3);
    env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.3 + 0.2);
    osc.connect(env).connect(state.audioCtx.destination);
    osc.start(now + i * 0.3);
    osc.stop(now + i * 0.3 + 0.25);
  }
}

// ---- Thunder crack (lightning strike) ----
export function playThunderSound() {
  if (!state.audioCtx) return;
  const now = state.audioCtx.currentTime;
  // Rumble noise
  const noise = state.audioCtx.createBufferSource();
  const buf = state.audioCtx.createBuffer(1, state.audioCtx.sampleRate * 0.5, state.audioCtx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) {
    d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.2);
  }
  noise.buffer = buf;
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.25, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  const lp = state.audioCtx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 800;
  noise.connect(lp).connect(env).connect(state.audioCtx.destination);
  noise.start(now);
  noise.stop(now + 0.6);

  // High crack
  const crack = state.audioCtx.createOscillator();
  crack.type = 'sawtooth';
  crack.frequency.value = 3000;
  const crackEnv = state.audioCtx.createGain();
  crackEnv.gain.setValueAtTime(0.08, now);
  crackEnv.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  crack.connect(crackEnv).connect(state.audioCtx.destination);
  crack.start(now);
  crack.stop(now + 0.1);
}

// ---- Void entry descending tone (phase 4 activation) ----
export function playVoidEntrySound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, now);
  osc.frequency.linearRampToValueAtTime(60, now + 1.5);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.1, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 1.6);
}

// ---- Abstraction attack warning (rising sawtooth) ----
export function playAbsWarningSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(200, now);
  osc.frequency.linearRampToValueAtTime(800, now + 0.3);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.06, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.4);
}

// ---- Abstraction attack noise burst ----
export function playAbsAttackSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const noise = state.audioCtx.createBufferSource();
  const buf = state.audioCtx.createBuffer(1, state.audioCtx.sampleRate * 0.3, state.audioCtx.sampleRate);
  const d = buf.getChannelData(0);
  for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 1.5);
  noise.buffer = buf;
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.2, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  const bp = state.audioCtx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1500;
  noise.connect(bp).connect(env).connect(state.audioCtx.destination);
  noise.start(now); noise.stop(now + 0.35);
}

// ---- Laser sweep sound (parameterised by duration) ----
export function playLaserSweepSound(duration) {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(300, now);
  osc.frequency.linearRampToValueAtTime(1200, now + duration * 0.5);
  osc.frequency.linearRampToValueAtTime(300, now + duration);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.04, now);
  env.gain.linearRampToValueAtTime(0.06, now + duration * 0.5);
  env.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + duration + 0.1);
}

// ---- Shockwave ring sound ----
export function playShockwaveSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(400, now);
  osc.frequency.exponentialRampToValueAtTime(80, now + 0.8);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.1, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.9);
}

// ---- Phase 5 dramatic entry hit ----
export function playPhase5EntrySound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  // Big dramatic hit
  const osc1 = state.audioCtx.createOscillator();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(80, now);
  osc1.frequency.linearRampToValueAtTime(200, now + 0.5);
  const env1 = state.audioCtx.createGain();
  env1.gain.setValueAtTime(0.15, now);
  env1.gain.exponentialRampToValueAtTime(0.001, now + 1);
  osc1.connect(env1).connect(state.audioCtx.destination);
  osc1.start(now); osc1.stop(now + 1.1);
  // Rising sweep
  const osc2 = state.audioCtx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(200, now);
  osc2.frequency.exponentialRampToValueAtTime(800, now + 1.5);
  const env2 = state.audioCtx.createGain();
  env2.gain.setValueAtTime(0.08, now);
  env2.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
  osc2.connect(env2).connect(state.audioCtx.destination);
  osc2.start(now); osc2.stop(now + 1.6);
}

// ---- Victory fanfare (win screen) ----
export function playVictoryFanfare() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const melody = [523.25, 659.25, 783.99, 1046.5];
  melody.forEach((freq, i) => {
    const osc = state.audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const env = state.audioCtx.createGain();
    env.gain.setValueAtTime(0, now + i * 0.2);
    env.gain.linearRampToValueAtTime(0.12, now + i * 0.2 + 0.05);
    env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.2 + 0.8);
    osc.connect(env).connect(state.audioCtx.destination);
    osc.start(now + i * 0.2); osc.stop(now + i * 0.2 + 0.9);
  });
  // Big final chord
  [523.25, 659.25, 783.99, 1046.5].forEach(freq => {
    const osc = state.audioCtx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const env = state.audioCtx.createGain();
    env.gain.setValueAtTime(0, now + 0.9);
    env.gain.linearRampToValueAtTime(0.08, now + 1.1);
    env.gain.exponentialRampToValueAtTime(0.001, now + 3);
    osc.connect(env).connect(state.audioCtx.destination);
    osc.start(now + 0.9); osc.stop(now + 3.1);
  });
}

// ---- Life lost descending tone ----
export function playLifeLostSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  const osc = state.audioCtx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(400, now);
  osc.frequency.linearRampToValueAtTime(100, now + 0.4);
  const env = state.audioCtx.createGain();
  env.gain.setValueAtTime(0.15, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
  osc.connect(env).connect(state.audioCtx.destination);
  osc.start(now); osc.stop(now + 0.5);
}

// ---- Shield pickup arpeggio ----
export function playShieldPickupSound() {
  if (!state.audioCtx || !state.sfxEnabled) return;
  const now = state.audioCtx.currentTime;
  [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
    const osc = state.audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const env = state.audioCtx.createGain();
    env.gain.setValueAtTime(0, now + i * 0.06);
    env.gain.linearRampToValueAtTime(0.1, now + i * 0.06 + 0.02);
    env.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.2);
    osc.connect(env).connect(state.audioCtx.destination);
    osc.start(now + i * 0.06); osc.stop(now + i * 0.06 + 0.25);
  });
}
